import NDRIcon from "@assets/ndrImages/ndr2026-title-icon.svg";
import { NDR_PAGE_URL_ALIAS } from "@config/ndrConstants";
import { AdsImu2 } from "@elements/Advertisement/variant/AdsImu";
import { getGTMData, getMetaData } from "@helper/getMetaData";
import BHBasePage, { type BHBasePageProps } from "@routes/BHBasePage";
import ErrorPage from "@routes/ErrorPage";
import WidgetWrapper from "@routes/Home/HomePageLayout/WidgetWrapper";
import NotFound from "@routes/NotFound";
import { Heading } from "@sphtech/sph-web-ui/elements/Heading";
import type { TRouteWithoutRedirect } from "@sphtech/web2-core/ssr";
import { ResponseType } from "@sphtech/web2-core/ssr";
import { USFEventSchema, USFEventSchemaProps } from "@sphtech/web2-seo-schema";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import { useLoaderData } from "react-router";

import LiveBlog from "./Components/LiveBlog";
import SeasonalKeywordArticleList from "./Components/SeasonalKeywordArticleList";
import { getNdpNdrMeta } from "./helper/getNdpNdrMeta";
import {
  fetchSeasonalTagData,
  GEPayloadResponseType,
} from "./SeasonalTags.server";
import SeasonalTagsMetaData from "./SeasonalTagsMetaData";

const adsSectionName = NDR_PAGE_URL_ALIAS;

export function meta() {
  const { title, description, name } = getNdpNdrMeta();
  return [
    getMetaData(
      SeasonalTagsMetaData(adsSectionName, title, description, name).metaTags,
    ),
  ];
}

export default function SeasonalTags() {
  const dataLoaderResponse: TRouteWithoutRedirect<
    GEPayloadResponseType,
    string
  > = useLoaderData();

  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    return <NotFound />;
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    return <ErrorPage message="Something went wrong" />;
  }

  const { tagsData, liveBlog } = dataLoaderResponse.payload;
  const pageAdTargetValue = PageAdTargetingTypeEnum.LISTING;

  const { h1, name, startDate, endDate } = getNdpNdrMeta();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Event",
    name,
    startDate,
    endDate,
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/MixedEventAttendanceMode",
    location: {
      "@type": "Place",
      name: "Singapore",
      address: {
        "@type": "PostalAddress",
        streetAddress: "1000 Toa Payoh North",
        addressLocality: "Singapore",
        postalCode: "318994",
        addressCountry: "Singapore",
      },
    },
  } as USFEventSchemaProps;

  const basePageProps: BHBasePageProps = {
    uniqueSectionName: adsSectionName,
    pageAdTargetValue: pageAdTargetValue,
    hasLb1Ads: true,
    showNDRBanner: false,
    bodyWrapperStyle: "my-0 lg:my-0 py-3 lg:py-md",
    hideQnFloatButton: true,
  };

  return (
    <>
      <USFEventSchema {...jsonLd} />
      <BHBasePage {...basePageProps}>
        <WidgetWrapper>
          <div>
            <div className="border-b-[1px] border-neutral-500 pb-[10px] md:pb-[20px] mb-[20px] md:mb-[30px] xl:mb-[40px]">
              <div className="flex gap-3 items-center">
                <img
                  src={NDRIcon}
                  alt={`${name.replace(/\s+/g, "-")}-icon`}
                  className="md:w-[46px] md:h-[50px] w-8 h-8"
                />
                <Heading
                  className="text-[#F62455] text-[18px] md:text-[30px] font-bold font-secondary"
                  Element="h1"
                >
                  {h1}
                </Heading>
              </div>
            </div>
          </div>
          <div
            className={`w-full flex flex-col xl:flex-row-reverse xl:w-[1187px] gap-5 xl:gap-10 ${liveBlog ? "lg:w-[992px]" : ""}`}
          >
            <div className="flex flex-col-reverse xl:flex-col gap-5 xl:w-[300px]">
              <div className="xl:flex xl:h-[600px] py-5 xl:py-0">
                <AdsImu2
                  uniqueSectionName={adsSectionName}
                  pageAdTargetType={PageAdTargetingTypeEnum.LISTING}
                />
              </div>
              {liveBlog && (
                <div className="mx-2.5 lg:mx-0 xl:block xl:flex-grow-no-shrink">
                  <LiveBlog seasonalLiveBlogContent={liveBlog} />
                </div>
              )}
            </div>
            <SeasonalKeywordArticleList
              articles={tagsData}
              adsSectionName={adsSectionName}
            />
          </div>
        </WidgetWrapper>
      </BHBasePage>
    </>
  );
}

export async function loader() {
  const data = await fetchSeasonalTagData();
  const { title, description, name } = getNdpNdrMeta();
  const { gaData } = SeasonalTagsMetaData(
    adsSectionName,
    title,
    description,
    name,
  );
  return {
    ...data,
    gtm: getGTMData(gaData),
  };
}
